import { V2CollectionItemSettings } from "@pages/CollectionsPage/V2CollectionItemSettings/V2CollectionItemSettings";
import { getAllGlobalCollections } from "@pages/CollectionsPage/hooks/useCollections";
import { loadAllCollectionItems } from "@pages/CollectionsPage/hooks/useGlobalCollectionItems";
import { CollectionItemSettings } from "@pages/Property/PropertyCollectionPage/CollectionItemPage/CollectionItemSettings/CollectionItemSettings";
import SubFolderDropdownWrapper from "@components/RelativesEditorPopup/SubFolderDropdownWrapper/SubFolderDropdownWrapper";
import { SearchInput } from "@components/ui-components/Inputs/SearchInput/SearchInput";
import { PathSegment } from "@src/data-hooks/documentation-collection/useDocumentationCollection";
import { FolderMapping } from "@pages/FilesPage/FilesPageList/ExpandedFileElement/ExpandedFileElement.types";
import { CompanyCollectionItemData } from "@src/to-be-removed/LocalData/Company/Collections/CompanyCollectionItemData";
import { Utilities } from "@utils/utilities";
import { doc, getFirestore } from "firebase/firestore";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useEffectOnce } from "react-use";
import { DeviationData } from "../../../to-be-removed/LocalData/Deviation/DeviationData";
import { SubtaskData } from "../../../to-be-removed/LocalData/Deviation/SubtaskData";
import { FirebaseData } from "../../../to-be-removed/LocalData/General/FirebaseData";
import { LocalData } from "../../../to-be-removed/LocalData/General/LocalData";
import { RelativeData, RelativeDataType } from "../../../to-be-removed/LocalData/General/RelativeData";
import { V2CollectionItemData } from "../../../to-be-removed/LocalData/General/V2CollectionItemData";
import { PropertyCollectionData } from "../../../to-be-removed/LocalData/Property/Collections/PropertyCollectionData";
import { PropertyCollectionItemData } from "../../../to-be-removed/LocalData/Property/Collections/PropertyCollectionItemData";
import { PropertyData } from "../../../to-be-removed/LocalData/Property/PropertyData";
import { UnitData } from "../../../to-be-removed/LocalData/Property/Units/UnitData";
import { RoutineData } from "../../../to-be-removed/LocalData/Routines/RoutineData";
import { CollectionData } from "../../../to-be-removed/LocalData/collections/CollectionData";
import { Spacer } from "../../ui-components/Spacer/Spacer";
import { Icon } from "../Icon/Icon";
import { ModalPopup } from "../ModalPopup/ModalPopup";
import { Popup } from "../Popup/Popup";
import { PropertySelector } from "../PropertySelector/PropertySelector";
import { Text } from "../Text/Text";
import styles from "./RelativesEditorPopup.module.css";
import { IRelatives, ITask } from "@propely/propely-typings";
import { StringUtilities } from "@src/utils/stringUtilities";
import { AriaButton } from "@components/ui-components/Buttons/AriaButton/AriaButton";
import { Plus } from "@icons/untitled_ui_icons";
import { useTasks } from "@src/data-hooks/task/task";

const filterAndSortItems = (items: SelectableMappedItem[] | undefined, searchText: string) =>
  items
    ?.filter((item) => !(item.firebaseData as any)?.archived && item.name.toLowerCase().includes(searchText.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

type ItemDataType = PropertyCollectionItemData | CompanyCollectionItemData;
type SelectableItem =
  | PropertyCollectionItemData
  | V2CollectionItemData
  | UnitData
  | ITask
  | RoutineData
  | SubtaskData
  | ItemDataType
  | CompanyCollectionItemData;
type SelectableMappedItem = { id: string; name: string; firebaseData?: FirebaseData; reference: string };

type Props = {
  id: string;
  didSelectRelatives?: (relatives: IRelatives) => void;
  relatives?: RelativeData[];
  property?: PropertyData;
  allowSelectingTenants?: boolean;
  allowSelectingTasks?: boolean;
  allowSelectingProperty?: boolean;
  allowSelectingForeignCollectionItems?: boolean;
  allowedProperties?: PropertyData[];
  allowSelectingGlobalCollectionItems?: boolean;
  label?: string;
  connectFrom: "file" | "task" | "other";
  onSelectFolders?: (relatives: { [relativeId: string]: { folder: PathSegment | undefined } }) => void;
  folderMapping?: FolderMapping;
};

export const RelativesEditorPopup = ({ allowSelectingForeignCollectionItems = true, ...props }: Props) => {
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedSubPage, setSelectedSubPage] = useState<PropertyCollectionData | CollectionData | "selected" | "tenants" | "tasks">("selected");
  const [selectedItems, setSelectedItems] = useState<SelectableMappedItem[]>([]);
  const [searchText, setSearchText] = useState("");

  const [property, setProperty] = useState(props.property);

  const isPropertySelectable = !property || props.allowSelectingProperty;
  const [expandedFolders, setExpandedFolders] = useState<{ [collectionItemId: string]: boolean }>({});
  const [folderMapping, setFolderMapping] = useState<{ [relativeId: string]: { folder: PathSegment | undefined } }>({});

  const { data: tasks } = useTasks({ propertyID: property?.id, archived: false }, { enabled: selectedSubPage === "tasks" });

  const collections =
    property?.collections.documents
      .filter(
        (collection) =>
          !collection.archived &&
          (props.connectFrom !== "file" || collection.connections.propertyFiles) &&
          (props.connectFrom !== "task" || collection.connections.deviations)
      )
      .sort((a, b) => Utilities.removeEmojis(a.name.translate()).toString().localeCompare(Utilities.removeEmojis(b.name.translate()).toString())) ??
    [];

  const mapItem = (item: SelectableItem): SelectableMappedItem => ({
    id: item.id,
    name:
      "deviationNumber" in item
        ? "#" + item.deviationNumber + " " + item.title
        : item instanceof RoutineData || item instanceof SubtaskData
          ? item.title
          : "name" in item
            ? item.name
            : "error",
    firebaseData: item instanceof FirebaseData ? item : undefined,
    reference: item instanceof FirebaseData ? item.getCompletePath() : `properties/${item.propertyID}/deviations/${item.id}`,
  });

  const items: SelectableMappedItem[] = useMemo(
    () =>
      selectedSubPage === "tenants"
        ? filterAndSortItems(
            property?.units.map((u) => mapItem(u)),
            searchText
          )
        : selectedSubPage === "tasks"
          ? filterAndSortItems(
              tasks?.map((d) => mapItem(d)),
              searchText
            )
          : selectedSubPage === "selected"
            ? selectedItems
            : selectedSubPage instanceof CollectionData
              ? filterAndSortItems(
                  LocalData.collectionItems.documents
                    .filter((item) => item.collection === selectedSubPage && (!property || item.properties.includes(property)))
                    .map((item) => mapItem(item)),
                  searchText
                )
              : filterAndSortItems(
                  selectedSubPage?.items.documents.map((u) => mapItem(u)),
                  searchText
                ),
    [property?.units, searchText, selectedItems, selectedSubPage, lastUpdate, tasks]
  );

  useEffect(() => {
    setSelectedSubPage("selected");
    property?.loadCollections().then(() => {
      setLastUpdate(new Date());
    });

    if (props.relatives) {
      Promise.all(props.relatives.map((relative) => relative.reference?.load())).finally(() => {
        const relativesWithReference =
          props.relatives?.filter(
            (relative) => StringUtilities.getPropertyIDFromPath(relative.reference?.getCompletePath() ?? "") === property?.id
          ) ?? [];

        setSelectedItems(
          relativesWithReference.map((relative) => ({
            id: relative.reference!.id,
            firebaseData: relative.reference!,
            name: relative.name,
            reference: relative.reference?.getCompletePath() ?? "",
          })) ?? []
        );
      });
    }
  }, [property, props.relatives]);

  useEffectOnce(() => {
    if (LocalData.moduleActivatedOnAnyProperties("globalCollections")) {
      getAllGlobalCollections().then(() => setLastUpdate(new Date()));
    }
  });

  useEffect(() => {
    if (selectedSubPage instanceof PropertyCollectionData) {
      selectedSubPage.loadAllItems().then(() => {
        setLastUpdate(new Date());
      });
    } else if (selectedSubPage instanceof CollectionData) {
      loadAllCollectionItems(selectedSubPage).then(() => {
        setLastUpdate(new Date());
      });
    }
  }, [selectedSubPage]);

  useEffect(() => {
    if (selectedSubPage === "selected") {
      Promise.all(selectedItems.filter((item) => !item.firebaseData?.loaded).map((item) => item.firebaseData?.load())).then(() => {
        setLastUpdate(new Date());
      });
    }
  }, [selectedItems, selectedSubPage]);

  function didFinish() {
    const relatives: { [key: string]: any } = {};
    for (const item of selectedItems) {
      relatives[item.id] = {
        type:
          item.firebaseData?.collectionName === "units"
            ? RelativeDataType.TENANT
            : item.reference.includes("deviations")
              ? RelativeDataType.DEVIATION
              : RelativeDataType.COLLECTION_ITEM,
        reference: doc(getFirestore(), item.reference),
        name: item instanceof DeviationData ? item.title : item.name,
      };
    }
    if (props.onSelectFolders) {
      props.onSelectFolders(folderMapping);
    }
    props.didSelectRelatives?.(relatives);
  }

  const createNewItem = () => {
    if (selectedSubPage instanceof PropertyCollectionData) {
      Popup.present((id) => (
        <CollectionItemSettings id={id} collection={selectedSubPage} didSave={(item) => setSelectedItems((items) => [mapItem(item), ...items])} />
      ));
    }
    if (selectedSubPage instanceof CollectionData) {
      Popup.present((id) => (
        <V2CollectionItemSettings
          id={id}
          collection={selectedSubPage}
          didSave={(item) => setSelectedItems((items) => [mapItem(item), ...items])}
          defaultProperties={props.property ? [props.property] : undefined}
        />
      ));
    }
  };

  const selectedTenantsCount = selectedItems.filter((item) => item.reference.includes("units")).length;
  const selectedDeviationsCount = selectedItems.filter((item) => item.reference.includes("deviations")).length;

  const toggleFolder = (collectionItemId: string) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [collectionItemId]: !prev[collectionItemId],
    }));
  };

  const handleSelectedFolder = (relativeId: string, folderPath: PathSegment | undefined) => {
    setFolderMapping((prev) => ({
      ...prev,
      [relativeId]: {
        folder: folderPath,
      },
    }));
  };

  return (
    <ModalPopup id={props.id} title={t("documentAI.connections.editConnections")} onActionClicked={didFinish}>
      <div>
        {isPropertySelectable && (
          <div style={{ marginBottom: "1em" }}>
            <PropertySelector
              width="100%"
              properties={props.allowedProperties ?? LocalData.currentUser.properties}
              optionsSelected={property && [property]}
              changedSelectedProperties={(p) => setProperty(p[0])}
              placeholder={t("main.selectProperty")}
              isMulti={false}
              dataCy="connections-property-select"
            />
          </div>
        )}

        {props.label && (
          <Text marginBottom={16} textAlign="left">
            {props.label}
          </Text>
        )}

        <div className={styles.container}>
          <div>
            {selectedItems.length > 0 && (
              <>
                <div className={styles.row} onClick={() => setSelectedSubPage("selected")}>
                  <Text size="md" fontWeight={500}>
                    {t("main.selectedConnections")}
                  </Text>

                  <Spacer />
                  {selectedItems.length > 0 && (
                    <Text size="sm" marginLeft={16} color="secondary">
                      {selectedItems.length.toString()}
                    </Text>
                  )}
                  <Icon name="chevron_right" marginLeft={4} />
                </div>
                <hr />
              </>
            )}
            {props.allowSelectingTenants && property && (
              <div className={`${styles.row} ${selectedSubPage === "tenants" ? styles.selected : ""}`} onClick={() => setSelectedSubPage("tenants")}>
                <Text size="md">{t("main.tenants")}</Text>
                <Spacer />
                {selectedTenantsCount > 0 && (
                  <Text size="sm" marginLeft={16} color="secondary">
                    {selectedTenantsCount.toString()}
                  </Text>
                )}
                <Icon name="chevron_right" marginLeft={4} />
              </div>
            )}
            {props.allowSelectingTasks && (
              <div className={`${styles.row} ${selectedSubPage === "tasks" ? styles.selected : ""}`} onClick={() => setSelectedSubPage("tasks")}>
                <Text size="md">{t("main.tasks")}</Text>
                <Spacer />
                {selectedDeviationsCount > 0 && (
                  <Text size="sm" marginLeft={16} color="secondary">
                    {selectedDeviationsCount.toString()}
                  </Text>
                )}
                <Icon name="chevron_right" marginLeft={4} />
              </div>
            )}
            {(property?.id === props.property?.id || allowSelectingForeignCollectionItems) && (
              <>
                {collections.map((collection, index) => {
                  const selectdItemCount = selectedItems.filter((item) => item.firebaseData?.parent === collection).length;
                  return (
                    <div
                      className={`${styles.row} ${selectedSubPage === collection ? styles.selected : ""}`}
                      onClick={() => setSelectedSubPage(collection)}
                      key={collection.id}
                      data-cy={`connections-collection-${index}`}
                    >
                      <Text size="md">{collection.name.translate()}</Text>
                      <Spacer />
                      {selectdItemCount > 0 && (
                        <Text size="sm" marginLeft={16} color="secondary">
                          {selectdItemCount.toString()}
                        </Text>
                      )}
                      <Icon name="chevron_right" marginLeft={4} />
                    </div>
                  );
                })}
              </>
            )}
            {props.allowSelectingGlobalCollectionItems && LocalData.moduleActivatedOnAnyProperties("globalCollections") && (
              <>
                {LocalData.collections.documents.map((collection) => {
                  const selectedItemCount = selectedItems.filter(
                    (item) => item.firebaseData instanceof V2CollectionItemData && item.firebaseData.collection === collection
                  ).length;
                  return (
                    <div
                      className={`${styles.row} ${selectedSubPage === collection ? styles.selected : ""}`}
                      onClick={() => setSelectedSubPage(collection)}
                      key={collection.id}
                    >
                      <Text size="md">{collection.name.translate()}</Text>
                      <Spacer />
                      {selectedItemCount > 0 && (
                        <Text size="sm" marginLeft={16} color="secondary">
                          {selectedItemCount.toString()}
                        </Text>
                      )}
                      <Icon name="chevron_right" marginLeft={4} />
                    </div>
                  );
                })}
              </>
            )}
          </div>
          <div className={styles.splitter} />
          <div className={styles.rightPanel}>
            {selectedSubPage && <SearchInput value={searchText} onChange={setSearchText} />}
            {(selectedSubPage instanceof PropertyCollectionData || selectedSubPage instanceof CollectionData) && (
              <AriaButton data-cy="create-new-item-button" variant="tertiary" onPress={createNewItem}>
                <Plus />
                {t("main.createNew")}
              </AriaButton>
            )}
            {items?.map((item, index) => {
              const isSelected = selectedItems.some((i) => i.reference === item.reference);
              return (
                <div key={item.id}>
                  <div
                    className={styles.row}
                    data-cy={`connections-collection-item-checkbox-${index}`}
                    onClick={() =>
                      isSelected
                        ? setSelectedItems((items) => items.filter((i) => i.reference !== item.reference))
                        : setSelectedItems((items) => [...items, item])
                    }
                  >
                    <Text size="md">{item instanceof DeviationData ? "#" + item.deviationNumber + " " + item.title : item.name}</Text>
                    <Spacer />
                    <Icon name={isSelected ? "check_box" : "check_box_outline_blank"} />
                    {(selectedSubPage instanceof PropertyCollectionData || selectedSubPage instanceof CollectionData) &&
                      item.firebaseData instanceof PropertyCollectionItemData && (
                        <Icon
                          name={expandedFolders[item.id] ? "expand_more" : "chevron_right"}
                          marginLeft={4}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFolder(item.id);
                          }}
                        />
                      )}
                  </div>
                  {property && expandedFolders[item.id] && item.firebaseData instanceof PropertyCollectionItemData && (
                    <SubFolderDropdownWrapper
                      collectionId={
                        selectedSubPage instanceof PropertyCollectionData || selectedSubPage instanceof CollectionData ? selectedSubPage.id : ""
                      }
                      propertyId={property.id}
                      collectionItemId={item.id}
                      onSelectFolder={handleSelectedFolder}
                      selectedFolderId={props.folderMapping?.[item.id]?.id}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </ModalPopup>
  );
};
